// Plan gate. The gateway refuses paid apps' content to free accounts (HTTP 402); this makes that visible.
//  - A free account can open the app's main page (and the sign-in pages) but nothing inside it: any other page
//    shows the upgrade notice instead, whether it was reached by address or by clicking around.
//  - The answer is never remembered: every check bypasses the browser cache, and while access is refused the
//    check repeats every few seconds (and whenever the tab is focused), so the moment the plan is upgraded - even
//    in another tab - the page reloads straight into the app. No cookie or storage clearing is needed.
//  - If the check itself can't be completed, content stays closed (it never fails open).
//  - A 402 from any API call is treated as "not included in this plan".
// This is the visible half; the gateway enforces the plan on every request regardless.
const GATEWAY = process.env.REACT_APP_GATEWAY_URL;
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
// The dashboard lives at the root of the login site (e.g. https://host/dashboard); /login/dashboard still redirects there.
const dashboard = (page) => `${new URL(LOGIN_URL).origin}/dashboard${page ? `?page=${page}` : ""}`;
const RECHECK_OK_MS = 3 * 60 * 1000;   // plan lapsed mid-session?
const RECHECK_BLOCKED_MS = 8 * 1000;   // plan upgraded meanwhile?

// What a free account may still see: the main page and the pages that sign people in or explain the product.
const OPEN_PATHS = ["/", "/login", "/register", "/signup", "/sso/callback", "/forgot-password", "/reset-password", "/families", "/schools", "/for-schools", "/how-to-use"];
const isOpenPath = (p = window.location.pathname) => {
  const path = p.length > 1 ? p.replace(/\/+$/, "") : p;
  return OPEN_PATHS.some((o) => path === o || (o !== "/" && path.startsWith(`${o}/`)));
};

let blocked = null;      // null = allowed; otherwise { info } (refused) or { error: true } (couldn't verify)
let product = null;
let getTokenFn = () => null;
let guard = null;

function removeNotice() {
  guard?.disconnect();
  guard = null;
  document.getElementById("hv-plan-gate")?.remove();
  const root = document.getElementById("root");
  if (root) root.style.display = "";
}

function link(label, href, primary) {
  const a = document.createElement("a");
  a.href = href;
  a.textContent = label;
  a.style.cssText = primary
    ? "display:inline-block;border:0;cursor:pointer;background:#1e3f33;color:#fff;text-decoration:none;font:600 16px system-ui,sans-serif;padding:12px 24px;border-radius:999px;margin:4px"
    : "display:inline-block;color:#1e3f33;text-decoration:underline;font:600 15px system-ui,sans-serif;padding:12px 16px;margin:4px";
  return a;
}

function renderNotice(title, text, actions, onRetry) {
  removeNotice();
  const el = document.createElement("div");
  el.id = "hv-plan-gate";
  el.setAttribute("role", "alertdialog");
  el.style.cssText = "position:fixed;inset:0;z-index:2147483647;background:#f4f7f2;display:grid;place-items:center;padding:24px;font-family:system-ui,sans-serif;text-align:center";
  const box = document.createElement("div");
  box.style.cssText = "max-width:440px";
  const h = document.createElement("h1");
  h.textContent = title;
  h.style.cssText = "font-size:26px;color:#1e3f33;margin:0 0 12px";
  const p = document.createElement("p");
  p.textContent = text;
  p.style.cssText = "color:#4a5a52;line-height:1.6;margin:0 0 20px";
  box.append(h, p);
  if (onRetry) {
    const b = document.createElement("button");
    b.textContent = "Try again";
    b.onclick = onRetry;
    b.style.cssText = "border:0;cursor:pointer;background:#1e3f33;color:#fff;font:600 16px system-ui,sans-serif;padding:12px 24px;border-radius:999px;margin:4px";
    box.append(b);
  }
  actions.forEach(([label, href, primary]) => box.append(link(label, href, primary)));
  el.append(box);
  document.body.append(el);
  const root = document.getElementById("root");
  if (root) root.style.display = "none";
  // If someone deletes the notice or un-hides the app, put it straight back.
  guard = new MutationObserver(() => {
    if (!document.getElementById("hv-plan-gate")) renderNotice(title, text, actions, onRetry);
    else if (root && root.style.display !== "none") root.style.display = "none";
  });
  guard.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
}

// A slim bar on the main page so a free visitor knows why the rest is closed.
function renderBanner(info) {
  if (document.getElementById("hv-plan-banner")) return;
  const el = document.createElement("div");
  el.id = "hv-plan-banner";
  el.style.cssText = "position:fixed;left:0;right:0;bottom:0;z-index:2147483000;background:#1e3f33;color:#fff;display:flex;flex-wrap:wrap;gap:8px 16px;align-items:center;justify-content:center;padding:10px 16px;font:500 14px system-ui,sans-serif";
  const t = document.createElement("span");
  const name = info?.product_name || "This app";
  t.textContent = info?.restricted === "age" ? `${info.product_name || "This app"} is for older learners.` : info?.error ? "We couldn't verify your plan, so lessons are closed for now." : info?.trial_eligible ? `${name} is part of Premium - start your ${info.trial_days || 30}-day free trial to open the lessons.` : `${name} is part of the Premium and Family plans - upgrade to open the lessons.`;
  el.append(t);
  if (!info?.error && info?.can_upgrade !== false && info?.restricted !== "age") {
    const a = document.createElement("a");
    a.href = dashboard("plans");
    a.textContent = info?.trial_eligible ? "Start free trial" : "View plans";
    a.style.cssText = "background:#fff;color:#1e3f33;text-decoration:none;font-weight:700;padding:6px 14px;border-radius:999px";
    el.append(a);
  }
  document.body.append(el);
}

// Show what the current page deserves: the app, the app + banner (main page), or the notice (content).
function enforce() {
  if (!blocked) {
    removeNotice();
    document.getElementById("hv-plan-banner")?.remove();
    return;
  }
  if (isOpenPath()) {
    removeNotice();
    renderBanner(blocked.info || blocked);
    return;
  }
  document.getElementById("hv-plan-banner")?.remove();
  if (document.getElementById("hv-plan-gate")) return;
  if (blocked.error) {
    renderNotice("Couldn't verify your plan", "We couldn't check whether your plan includes this app. Please check your connection and try again.", [["Back to the main page", "/", false]], () => window.location.reload());
    return;
  }
  const info = blocked.info || {};
  const name = info.product_name || "This app";
  if (info.restricted === "age") {   // a young student: nothing to buy - this app is simply for older learners
    renderNotice("This app is for older learners", `${name} isn't available for your age group yet. The apps for your age group are ready for you on your dashboard.`,
      [["Back to dashboard", dashboard(""), true], ["Back to the main page", "/", false]]);
    return;
  }
  const plan = info.plan_name ? ` You're on the ${info.plan_name} plan.` : "";
  const canUpgrade = info.can_upgrade !== false;
  const trial = canUpgrade && info.trial_eligible;
  renderNotice(
    "Please upgrade your plan to access this",
    canUpgrade ? `${name} is part of the Premium and Family plans.${plan}${trial ? ` Try it free for ${info.trial_days || 30} days.` : ""}` : `${name} isn't included in your school's plan yet. Please ask your school administrator.`,
    [[canUpgrade ? (trial ? "Start free trial" : "View plans") : "Back to dashboard", dashboard(canUpgrade ? "plans" : ""), true], ["Back to the main page", "/", false]],
  );
}

// A refused call (402) is an expected answer, not a crash: the notice above is the response to it. Callers that don't
// catch it would otherwise surface as "Uncaught (in promise)" - and, in development, as the red error overlay on top
// of the notice. Capture phase + stopImmediatePropagation runs before the overlay's own listener.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    if (r && (r.response?.status === 402 || r.status === 402)) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
}

export function showUpgradeNotice(info = {}) {
  blocked = { info };
  enforce();
}

async function check(token) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000);
  try {
    // no-store + a throwaway query value: an earlier answer (e.g. "refused" from before the upgrade) is never reused.
    const r = await fetch(`${GATEWAY}/billing/access/${product}?_=${Date.now()}`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store", signal: ctrl.signal });
    if (r.status === 401 || r.status === 403) return { state: "signed-out" }; // expired token: the app's own sign-in flow handles it
    if (!r.ok) return { state: "error" };
    const info = await r.json();
    return info.allowed === false ? { state: "blocked", info } : { state: "ok" };
  } catch {
    return { state: "error" };
  } finally {
    clearTimeout(timer);
  }
}

async function recheck() {
  const t = getTokenFn();
  if (!t) return;
  const r = await check(t);
  if (r.state === "blocked") { blocked = { info: r.info }; enforce(); }
  else if (r.state === "ok" && blocked) window.location.reload(); // upgraded: start over with a clean slate
  else if (r.state === "ok") enforce();
}

// Resolves true once the app may mount. A refused account still mounts (main page only); the pages inside are covered by enforce().
export async function planGate({ product: p, getToken }) {
  product = p;
  getTokenFn = getToken;
  const token = getToken();
  if (!token || !GATEWAY) return true; // signed out: landing page / sign-in as usual

  const res = await check(token);
  if (res.state === "blocked") blocked = { info: res.info };
  else if (res.state === "error") blocked = { error: true };
  enforce();

  // Follow in-app navigation (react-router uses the History API) so a click from the main page into content is stopped.
  for (const fn of ["pushState", "replaceState"]) {
    const orig = window.history[fn];
    window.history[fn] = function (...args) { const out = orig.apply(this, args); enforce(); return out; };
  }
  window.addEventListener("popstate", enforce);

  const tick = () => { if (!document.hidden) recheck(); };
  const loop = () => setTimeout(() => { tick(); loop(); }, blocked ? RECHECK_BLOCKED_MS : RECHECK_OK_MS);
  loop();
  document.addEventListener("visibilitychange", tick);
  window.addEventListener("focus", tick);
  return true;
}
